/**
 * TodayChoreSummaryLink — today's chore count + /oppgavebunken link (Plan 03-09 D-12).
 *
 * Read-only count + nav link. Does NOT duplicate Phase 2 chore UI.
 * Chore count sourced from /api/chores?status=available (available pool).
 *
 * T-CAL-43: /i-dag is authenticated; 401 from /api/chores surfaces via error
 * state, count defaults to 0, shows empty copy.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';

function useTodayChoreCount() {
  return useQuery<number>({
    queryKey: ['chores', 'today-summary'],
    queryFn: async () => {
      const res = await fetch('/api/chores?status=available', { credentials: 'include' });
      if (!res.ok) throw new Error(`chores ${res.status}`);
      const json = (await res.json()) as { chores?: unknown[]; length?: number } | unknown[];
      // /api/chores returns an array directly per useChoresQuery.ts
      if (Array.isArray(json)) return json.length;
      const arr = (json as { chores?: unknown[] }).chores;
      return Array.isArray(arr) ? arr.length : 0;
    },
    staleTime: 60_000,
  });
}

export function TodayChoreSummaryLink({ count }: { count?: number }) {
  const { t } = useTranslation();
  const { data: fetchedCount = 0 } = useTodayChoreCount();
  const resolvedCount = count ?? fetchedCount;

  return (
    <section aria-label={t('calendar.today.sectionChores')} data-testid="today-chores">
      <h2 className="text-label font-semibold uppercase tracking-widest text-ink-2 mb-2">
        {t('calendar.today.sectionChores')} · <span className="tnum">{resolvedCount}</span>
      </h2>
      {resolvedCount === 0 ? (
        <p className="text-ink-2 text-sm" data-testid="today-chores-empty">
          {t('dashboard.chores.empty')}
        </p>
      ) : (
        <Link
          to="/oppgavebunken"
          className="text-primary text-sm font-medium"
          data-testid="today-chores-link"
        >
          {t('calendar.today.choresLink')}
        </Link>
      )}
    </section>
  );
}
